import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const viewport = event.matches ? 'desktop' : 'mobile';
    let storedLayout = sessionStorage.getItem(`product-grid-view-${viewport}`) || 'default';

    if (storedLayout === 'zoom-out') {
      storedLayout = 'columns-6';
      sessionStorage.setItem('product-grid-view-desktop', 'columns-6');
    }

    if (viewport === 'desktop' && storedLayout === 'with-banner') {
      storedLayout = 'default';
      sessionStorage.setItem('product-grid-view-desktop', 'default');
    }

    if (viewport === 'mobile' && storedLayout === 'mobile-4') {
      storedLayout = 'default';
      sessionStorage.setItem('product-grid-view-mobile', 'default');
    }

    const inputName = event.matches ? 'grid' : 'grid-mobile';
    const targetElement = this.querySelector(
      `input[type="radio"][name="${inputName}"][value="${storedLayout}"]`
    );

    if (targetElement instanceof HTMLInputElement) {
      targetElement.checked = true;
    }

    this.#setLayout(storedLayout);
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
